import React, { useNavigate } from "react-router-dom";
import OrderTable from "./componenets/OrderTable";
import { hasAddAccess } from "../../../../Common/CheckAccess";
import { useEffect, useState, useMemo } from "react";
import { Search, Filter, X, Calendar, User } from "lucide-react";

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Pending", value: "pending" },
];

const Order = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Enhanced filter states
  const [filters, setFilters] = useState({
    guest_name: "",
    restaurant_user_name: "",
    created_start_date: "",
    created_end_date: "",
  });
  const [filterType, setFilterType] = useState("Guest Name");
  const [globalFilter, setGlobalFilter] = useState("");
  
  const filterOptions = [
    "Guest Name", 
    "Restaurant User", 
    "Date Range"
  ];

  const existingPermissions = localStorage.getItem("checkPermission");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  // Reset filters when filter type changes
  useEffect(() => {
    setGlobalFilter("");
    setFilters({
      guest_name: "",
      restaurant_user_name: "",
      created_start_date: "",
      created_end_date: "",
    });
  }, [filterType]);

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      guest_name: "",
      restaurant_user_name: "",
      created_start_date: "",
      created_end_date: "",
    });
    setGlobalFilter("");
    setStatusFilter("all");
  };

  const hasActiveFilters = globalFilter || 
    filters.created_start_date || 
    filters.created_end_date || 
    statusFilter !== "all";

  // Create filter payload with useMemo to ensure stable reference and proper re-rendering
  const filterPayload = useMemo(() => {
    const payload = {
      status: statusFilter !== "all" ? statusFilter : undefined,
    };

    // Add filter parameters based on filter type and global filter
    if (globalFilter && globalFilter.trim()) {
      if (filterType === "Guest Name") {
        payload.guest_name = globalFilter.trim();
      } else if (filterType === "Restaurant User") {
        payload.restaurant_user_name = globalFilter.trim();
      }
    }

    // Date range filters
    if (filters.created_start_date) {
      payload.created_start_date = filters.created_start_date;
    }

    if (filters.created_end_date) {
      payload.created_end_date = filters.created_end_date;
    }

    console.log('Filter Payload Generated:', payload);
    return payload;
  }, [statusFilter, globalFilter, filterType, filters.created_start_date, filters.created_end_date]);

  return (
    <div className="container-fluid px-4 py-3">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="h3 mb-0 fw-bold">Order List</h1>
            {hasAddAccess(permissions, "restaurant") && (
              <button
                className="btn btn-primary"
                onClick={() => navigate("/restaurant/add-order")}
              >
                Create Order
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Filter Buttons */}
      <div className="row mb-4">
        <div className="col-6 d-flex flex-column align-items-center">
          <div
            className="px-4 py-3 h-100"
            style={{
              background: "linear-gradient(90deg, #f8fafc 0%, #e9f0ff 100%)",
              borderRadius: 18,
              border: "1.5px solid #e0e7ef",
              boxShadow: "0 2px 12px 0 rgba(80,120,200,0.06)",
            }}
          >
            <div className="d-flex align-items-center justify-content-center mb-2 gap-2">
              <i className="bi bi-funnel-fill text-primary" style={{ fontSize: 20 }}></i>
              <span className="fw-bold text-secondary" style={{ letterSpacing: 1, fontSize: 17 }}>
                Quick Status Filter
              </span>
            </div>
            <div className="d-flex gap-3 flex-wrap justify-content-center">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold transition-all border-0 ${
                    statusFilter === option.value
                      ? "btn-primary text-white shadow-lg scale-up"
                      : "btn-outline-primary bg-white text-primary shadow-sm"
                  }`}
                  style={{
                    minWidth: 90,
                    fontSize: 14,
                    boxShadow: statusFilter === option.value ? "0 4px 16px 0 rgba(80,120,200,0.13)" : undefined,
                    transform: statusFilter === option.value ? "scale(1.08)" : "scale(1)"
                  }}
                  onClick={() => setStatusFilter(option.value)}
                  onMouseEnter={e => e.currentTarget.style.background = statusFilter === option.value ? "#174ea6" : "#35b0dc"}
                  onMouseLeave={e => e.currentTarget.style.background = statusFilter === option.value ? "#2563eb" : "#fff"}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Filter - Right Side */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Search size={20} className="text-primary" />
                  <h6 className="mb-0 fw-semibold text-secondary">Search & Filter</h6>
                </div>
                
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleClearFilters}
                  >
                    <X size={14} className="me-1" />
                    Clear
                  </button>
                )}
              </div>

              <div className="row g-3">
                {/* Filter Type Selection */}
                <div className={`${filterType === "Date Range" ? "col-md-4" : "col-md-6"}`}>
                <label className="form-label fw-semibold text-muted">
                    <Filter size={16} className="me-1" />
                    Filter By
                  </label>
                  <select
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    {filterOptions.map((option, index) => (
                      <option value={option} key={index}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dynamic Search Input */}
                {filterType !== "Date Range" && (
                  <div className={`${filterType === "Date Range" ? "col-md-4" : "col-md-6"}`}>
                    <label className="form-label fw-semibold text-muted">
                      {filterType === "Guest Name" && <User size={16} className="me-1" />}
                      {filterType === "Restaurant User" && <User size={16} className="me-1" />}
                      {filterType}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={`Enter ${filterType.toLowerCase()}`}
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                  </div>
                )}

                {/* Date Range Filters */}
                {filterType === "Date Range" && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-muted">
                        <Calendar size={16} className="me-1" />
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.created_start_date || ""}
                        onChange={(e) => handleFilterChange("created_start_date", e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-muted">
                        <Calendar size={16} className="me-1" />
                        End Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.created_end_date || ""}
                        onChange={(e) => handleFilterChange("created_end_date", e.target.value)}
                        min={filters.created_start_date || ""}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="mt-3 pt-3 border-top">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <small className="text-muted me-2 fw-semibold">Active:</small>
                    
                    {statusFilter !== "all" && (
                      <span className="badge bg-primary px-2 py-1">
                        Status: {statusOptions.find(s => s.value === statusFilter)?.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-1"
                          style={{ fontSize: '6px' }}
                          onClick={() => setStatusFilter("all")}
                        ></button>
                      </span>
                    )}

                    {globalFilter && (
                      <span className="badge bg-info px-2 py-1">
                        {filterType}: {globalFilter}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-1"
                          style={{ fontSize: '6px' }}
                          onClick={() => setGlobalFilter("")}
                        ></button>
                      </span>
                    )}

                    {(filters.created_start_date || filters.created_end_date) && (
                      <span className="badge bg-success px-2 py-1">
                        Date: {filters.created_start_date || 'Any'} - {filters.created_end_date || 'Any'}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-1"
                          style={{ fontSize: '6px' }}
                          onClick={() => {
                            handleFilterChange("created_start_date", "");
                            handleFilterChange("created_end_date", "");
                          }}
                        ></button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <OrderTable 
                statusFilter={statusFilter} 
                additionalFilters={filterPayload}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Order;
